import { Suspense, lazy } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { LanguageProvider } from "@/hooks/use-language";
import { useAuthRedirect } from "@/hooks/use-auth-redirect";
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));

const Index = lazy(() => import("./pages/Index"));
const Compare = lazy(() => import("./pages/Compare"));
const Login = lazy(() => import("./pages/Login"));
const Signup = lazy(() => import("./pages/Signup"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const DashboardLayout = lazy(() => import("./pages/DashboardLayout"));
const DashboardHome = lazy(() => import("./pages/dashboard/DashboardHome"));
const WidgetBuilder = lazy(() => import("./pages/dashboard/WidgetBuilder"));
const EmbedCode = lazy(() => import("./pages/dashboard/EmbedCode"));
const Conversations = lazy(() => import("./pages/dashboard/Conversations"));
const Analytics = lazy(() => import("./pages/dashboard/Analytics"));
const KnowledgeBase = lazy(() => import("./pages/dashboard/KnowledgeBase"));
const FAQBuilder = lazy(() => import("./pages/dashboard/FAQBuilder"));
const SettingsPage = lazy(() => import("./pages/dashboard/SettingsPage"));
const Upgrade = lazy(() => import("./pages/dashboard/Upgrade"));
const Subscription = lazy(() => import("./pages/dashboard/Subscription"));
const Appointments = lazy(() => import("./pages/dashboard/Appointments"));
const MyWebsites = lazy(() => import("./pages/dashboard/MyWebsites"));
const DoneForYou = lazy(() => import("./pages/dashboard/DoneForYou"));
const Leads = lazy(() => import("./pages/dashboard/Leads"));
const RichMediaCards = lazy(() => import("./pages/dashboard/RichMediaCards"));
const WidgetPage = lazy(() => import("./pages/WidgetPage"));
const AdminTestPlans = lazy(() => import("./pages/AdminTestPlans"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

/** Inner component so useAuthRedirect has access to Router context */
const AppRoutes = () => {
  useAuthRedirect();

  return (
    <Suspense fallback={
      <div className="min-h-screen flex items-center justify-center" style={{
        background: window.location.pathname.startsWith("/widget/") ? "transparent" : undefined,
      }}>
        {!window.location.pathname.startsWith("/widget/") && (
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        )}
      </div>
    }>
      <Routes>
        <Route path="/" element={<Index />} />
        <Route path="/compare" element={<Compare />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/onboarding" element={<Onboarding />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/dashboard" element={<DashboardLayout />}>
          <Route index element={<DashboardHome />} />
          <Route path="widget" element={<WidgetBuilder />} />
          <Route path="embed" element={<EmbedCode />} />
          <Route path="conversations" element={<Conversations />} />
          <Route path="analytics" element={<Analytics />} />
          <Route path="knowledge" element={<KnowledgeBase />} />
          <Route path="faq" element={<FAQBuilder />} />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="appointments" element={<Appointments />} />
          <Route path="websites" element={<MyWebsites />} />
          <Route path="done-for-you" element={<DoneForYou />} />
          <Route path="leads" element={<Leads />} />
          <Route path="rich-media" element={<RichMediaCards />} />
          <Route path="upgrade" element={<Upgrade />} />
          <Route path="subscription" element={<Subscription />} />
        </Route>
        <Route path="/widget/chat" element={<WidgetPage />} />
        <Route path="/admin/test-plans" element={<AdminTestPlans />} />
        <Route path="/unsubscribe" element={<Unsubscribe />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <LanguageProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </LanguageProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
